import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../auth/store/authSlice';
import { fetchUnreadCount } from '../../notification/store/notificationSlice';
import authService from '../../auth/services/auth.service';
import toast from 'react-hot-toast';
import FixomeLogo from '../components/FixomeLogo';
import MobileSmartAppBanner from '../components/MobileSmartAppBanner';
import AppStoreBadges from '../components/AppStoreBadges';
import './fx-customer.css';
import { BookingDraftProvider, useBookingDraft } from '../../booking/bookingDraftContext';
import { bookingNavigationState } from '../../booking/utils/bookingNavigationState';
import { COPYRIGHT_YEAR } from '../copyrightYear';
import useAfterIdle from '../../../utils/useAfterIdle';

/**
 * MainLayout Component
 * Navigation bar and footer for the main app
 */
const MainLayout = ({ children }) => (
  <BookingDraftProvider>
    <MainLayoutShell>{children}</MainLayoutShell>
  </BookingDraftProvider>
);

const MainLayoutShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { unreadCount: notificationCount } = useSelector((state) => state.notification);
  const { draft } = useBookingDraft();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notificationsReady = useAfterIdle(2000);

  // Fetch unread count only for a signed-in user, and only after idle.
  useEffect(() => {
    if (isAuthenticated && notificationsReady) {
      // Initial fetch
      dispatch(fetchUnreadCount());
      
      // Poll when page is visible/focused
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          dispatch(fetchUnreadCount());
        }
      };
      
      // Poll when window regains focus
      const handleFocus = () => {
        dispatch(fetchUnreadCount());
      };
      
      // Set up event listeners
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      
      // Polling removed - notification count refreshes on visibility/focus changes above
      // Users can also click notification icon to refresh
      // This reduces API calls by ~95% while maintaining good UX
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [isAuthenticated, notificationsReady, dispatch, user?.email]);

  const handleLogout = async () => {
    try {
      // Call the logout API to clear server-side session
      await authService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
    }
    
    // Always dispatch logout action regardless of API result
    dispatch(logout());
    
    // Show success message
    toast.success('Logged out successfully');
    
    // Use window.location.href for immediate navigation to avoid ProtectedRoute interference
    window.location.href = '/';
  };

  const navigationItems = [
    { name: 'Home', path: '/', icon: '🏠' },
    { name: 'Offers', path: '/offers', icon: '🎁' },
    { name: 'Blog', path: '/blogs', icon: '📝' },
    { name: 'My Bookings', path: '/bookings', icon: '📋' },
    { name: 'Help', path: '/help', icon: '❓' },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <div className="fx-customer min-h-screen flex flex-col">
      {/* Mobile Smart App Download Banner */}
      <MobileSmartAppBanner />

      {/* Navigation Bar */}
      <nav className="bg-white border-b sticky top-0 z-50" style={{ borderColor: 'var(--fx-line, #eaecee)' }}>
        <div className="fx-nav-shell max-w-7xl mx-auto px-4 md:px-8">
          <div className="fx-nav-row flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="fx-nav-logo flex items-center space-x-2 transition-transform hover:scale-[1.02]">
              <FixomeLogo size="small" className="md:hidden" showTagline={false} />
              <FixomeLogo size="medium" className="hidden md:block" showTagline={false} />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-slate-100 text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="fx-nav-actions flex items-center gap-1 sm:gap-3 md:gap-5">
              {/* App Download Link - Desktop */}
              <a
                href="/#download-app"
                className="hidden lg:inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 hover:text-slate-900 transition-all duration-200 border border-slate-200/80"
              >
                <span>📱</span>
                <span>Get App</span>
              </a>

              {/* Country Flag - India */}
              <div className="hidden sm:flex items-center space-x-2 text-2xl lg:text-3xl opacity-80 hover:opacity-100 transition-opacity">
                <span>🇮🇳</span>
              </div>

              {/* Emergency stays reachable below sm — icon plus label, not display:none */}
              <button
                type="button"
                onClick={() => navigate('/booking/new/emergency', { 
                  state: { 
                    isEmergency: true,
                    urgency: 'emergency' 
                  } 
                })}
                className="fx-btn-emergency"
              >
                <span className="fx-em-icon" aria-hidden="true">🚨</span>
                <span>Emergency</span>
              </button>

              <button
                type="button"
                aria-label="Book a Service"
                onClick={() => navigate('/booking/new', {
                  state: bookingNavigationState({ intent: draft }),
                })}
                className="fx-btn-primary px-3 sm:px-5"
              >
                <span className="sm:hidden">Book</span>
                <span className="hidden sm:inline">Book a Service</span>
              </button>

              {isAuthenticated ? (
                <>
                  {/* Notifications */}
                  <button
                    type="button"
                    onClick={() => navigate('/notifications')}
                    className="fx-nav-bell relative p-2 text-secondary-700 hover:text-primary-500 transition-colors"
                    aria-label="Notifications"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    {notificationCount > 0 && (
                      <span className="absolute top-0 right-0 bg-error text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                        {notificationCount}
                      </span>
                    )}
                  </button>

                  {/* User Menu - Hidden on mobile, shown on desktop */}
                  <div className="relative hidden md:block" ref={userMenuRef}>
                    <button 
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold overflow-hidden">
                        {user?.profile?.name ? user.profile.name.charAt(0).toUpperCase() : (
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        )}
                      </div>
                      <svg
                        className={`w-4 h-4 text-secondary-700 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu - Account actions only */}
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200">
                        <Link
                          to="/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="block px-4 py-2 text-secondary-700 hover:bg-gray-100 transition-colors"
                        >
                          👤 My Profile
                        </Link>
                        <hr className="my-2 border-gray-200" />
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            handleLogout();
                          }}
                          className="block w-full text-left px-4 py-2 text-error hover:bg-gray-100 transition-colors"
                        >
                          🚪 Logout
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="fx-nav-login flex items-center">
                  <Link
                    to="/login"
                    className="fx-btn-secondary whitespace-nowrap"
                  >
                    Login
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="fx-nav-menu md:hidden p-2 text-secondary-700"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="fx-mobile-menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div id="fx-mobile-menu" className="md:hidden py-4 border-t animate-slide-up">
              {isAuthenticated ? (
                <>
                  {/* Home */}
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                      isActive('/')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">🏠</span>
                    <span>Home</span>
                  </Link>
                  
                  {/* My Profile */}
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                      isActive('/profile')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">👤</span>
                    <span>My Profile</span>
                  </Link>
                  
                  {/* Offers */}
                  <Link
                    to="/offers"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                      isActive('/offers')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">🎁</span>
                    <span>Offers</span>
                  </Link>

                  {/* Blog */}
                  <Link
                    to="/blogs"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                      location.pathname.startsWith('/blogs')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">📝</span>
                    <span>Blog</span>
                  </Link>

                  {/* My Bookings */}
                  <Link
                    to="/bookings"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                      isActive('/bookings')
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-secondary-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">📋</span>
                    <span>My Bookings</span>
                  </Link>

                  {/* Notifications — header bell moves here on the narrow band */}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/notifications');
                    }}
                    className="fx-nav-bell-menu items-center space-x-3 px-4 py-3 font-medium text-secondary-700 hover:bg-gray-50 w-full text-left transition-colors"
                  >
                    <span className="text-xl" aria-hidden="true">🔔</span>
                    <span>Notifications{notificationCount > 0 ? ` (${notificationCount})` : ''}</span>
                  </button>
                  
                  {/* Divider */}
                  <hr className="my-2 border-gray-200" />
                  
                  {/* Logout */}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center space-x-3 px-4 py-3 font-medium text-error hover:bg-red-50 w-full text-left transition-colors"
                  >
                    <span className="text-xl">🚪</span>
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 font-medium transition-colors ${
                        isActive(item.path)
                          ? 'text-primary-500 bg-primary-50'
                          : 'text-secondary-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                  {/* Download App */}
                  <a
                    href="/#download-app"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xl">📱</span>
                    <span>Download App</span>
                  </a>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="fx-nav-login-menu items-center space-x-3 px-4 py-3 font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-xl">👤</span>
                    <span>Login</span>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 mt-16 border-t-4" style={{ borderTopColor: 'var(--fx-primary, #e10600)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {/* About */}
            <div>
              <div className="mb-4">
                <FixomeLogo size="medium" className="[&>div>span]:!text-white [&>div>span:first-child]:!text-primary-400" />
              </div>
              <p className="text-white/80 text-base md:text-sm mb-4">
                Every Home Deserves Care, Every Fix Deserves Fixome
              </p>
              <div className="flex space-x-4">
                <a href="https://wa.me/917012689824" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-green-400 transition-colors" aria-label="WhatsApp">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </a>
                <a href="https://www.facebook.com/share/1ATQpi6hun/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-blue-400 transition-colors" aria-label="Facebook">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/fixome.in" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-pink-400 transition-colors" aria-label="Instagram">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>

              {/* App Store & Google Play Badges */}
              <div className="mt-6 pt-6 border-t border-slate-800/80">
                <p className="text-xs font-semibold text-white/90 uppercase tracking-wider mb-3">
                  Get the Fixome App
                </p>
                <AppStoreBadges variant="dark" size="compact" direction="col" />
              </div>
            </div>

            {/* Services */}
            <div>
              <h2 className="font-bold mb-4 text-base">Services</h2>
              <ul className="space-y-2 text-sm text-white/80">
                {[
                  ['Plumbing', '/booking/new/plumbing'],
                  ['Electrical', '/booking/new/electrical'],
                  ['AC Repair', '/booking/new/ac-repair'],
                  ['Deep Cleaning', '/booking/new/deep-cleaning'],
                  ['Pest Control', '/booking/new/pest-control'],
                  ['Moving Services', '/booking/new/moving'],
                  ['Painting', '/booking/new/painting'],
                  ['Appliance Repair', '/booking/new/appliance-repair-and-service'],
                ].map(([label, path]) => (
                  <li key={path}>
                    <Link to={path} className="hover:text-primary-400 transition-colors">{label}</Link>
                  </li>
                ))}
                <li>
                  <Link to="/services" className="hover:text-primary-400 transition-colors">All services</Link>
                </li>
                <li>
                  <Link to="/cities/kochi" className="hover:text-primary-400 transition-colors">Kochi</Link>
                </li>
                <li>
                  <Link to="/cities/calicut" className="hover:text-primary-400 transition-colors">Calicut</Link>
                </li>
                <li>
                  <Link to="/cities/thiruvananthapuram" className="hover:text-primary-400 transition-colors">Thiruvananthapuram</Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h2 className="font-bold mb-4 text-base">Company</h2>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/about" className="hover:text-primary-400 transition-colors">About Us</Link></li>
                <li><Link to="/blogs" className="hover:text-primary-400 transition-colors">Blog</Link></li>
                <li><Link to="/offers" className="hover:text-primary-400 transition-colors">Offers</Link></li>
                <li><Link to="/technician/apply" className="hover:text-primary-400 transition-colors">Register as Technician</Link></li>
                <li><Link to="/contact" className="hover:text-primary-400 transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h2 className="font-bold mb-4 text-base">Contact</h2>
              <ul className="space-y-2 text-sm text-white/80">
                <li>📞 <a href="tel:+917012689824" className="hover:text-primary-400 transition-colors">+91 70126 89824</a></li>
                <li>✉️ <a href="mailto:support@fixome.in" className="hover:text-primary-400 transition-colors">support@fixome.in</a></li>
                <li>💬 <a href="https://wa.me/917012689824" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors">WhatsApp</a></li>
                <li>🕒 Service Hours: 8am - 7pm</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 mt-8 pt-8 text-center text-sm text-white/60">
            <p>&copy; {COPYRIGHT_YEAR} Fixome. All rights reserved.</p>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              <Link to="/privacy" className="hover:text-primary-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary-400 transition-colors">Terms of Service</Link>
              <Link to="/razorpay-terms" className="hover:text-primary-400 transition-colors">Payment Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;

